// Layouts (Sprint 9, docs/09 §C1): reusable multi-zone screen layouts. Org-level
// config that lives in Settings next to overlays/brand; a screen picks one in its
// drawer. Create/edit uses a VISUAL preset picker (SVG rect compositions from
// PRESET_ZONES) and a playlist select per non-main zone. Video plays only in the
// main zone — the server enforces it (422), surfaced inline here.

import { useState } from 'react';
import toast from 'react-hot-toast';
import { Drawer } from '../../components/Drawer.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import { Button, Loading, TextField, EmptyState } from '../../components/ui.tsx';
import { ApiError } from '../../api/client.ts';
import { usePermissions } from '../../auth/session.tsx';
import { usePlaylists } from '../../api/playlists.ts';
import {
  useLayouts,
  useCreateLayout,
  useUpdateLayout,
  useDeleteLayout,
  type Layout,
  type LayoutZones,
} from '../../api/layouts.ts';
import {
  LAYOUT_PRESETS,
  PRESET_LABELS,
  ZONE_LABELS,
  nonMainZonesForPreset,
  type LayoutPreset,
} from '../../lib/layout-presets.ts';
import { PresetDiagram } from './PresetDiagram.tsx';

/** Summarize a layout's non-main zone assignments as "Ticker → Base Loop" chips. */
function zoneSummary(layout: Layout, playlistName: (id: string) => string): string {
  const parts = nonMainZonesForPreset(layout.preset).map((z) => {
    const pid = layout.zones[z];
    return `${ZONE_LABELS[z]} → ${pid ? playlistName(pid) : 'None'}`;
  });
  return parts.length ? parts.join(' · ') : 'Single full-screen zone';
}

export function LayoutsSection() {
  const perms = usePermissions();
  const layouts = useLayouts();
  const playlists = usePlaylists();
  const del = useDeleteLayout();
  const [editing, setEditing] = useState<Layout | 'new' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Layout | null>(null);

  const playlistName = (id: string) => playlists.data?.find((p) => p.id === id)?.name ?? '(removed)';

  return (
    <div className="card card-pad">
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="card-title">Layouts</div>
          <p className="hint">Split a screen into zones — a main area plus a ticker or side panel.</p>
        </div>
        {perms.canManageContent && (
          <Button variant="primary" onClick={() => setEditing('new')}>
            New layout
          </Button>
        )}
      </div>

      {layouts.isLoading ? (
        <Loading />
      ) : (layouts.data ?? []).length === 0 ? (
        <EmptyState
          icon={<PresetDiagram preset="main_side" />}
          title="No layouts yet"
        >
          <p className="text-dim">
            A layout divides a screen into zones. Create one, then assign it to a screen from its
            details panel.
          </p>
        </EmptyState>
      ) : (
        <div className="layout-list mt-2">
          {layouts.data!.map((l) => (
            <div key={l.id} className="layout-row">
              <div className="layout-row-glyph" aria-hidden>
                <PresetDiagram preset={l.preset} />
              </div>
              <div className="layout-row-body">
                <div className="cell-strong">{l.name}</div>
                <div className="cell-dim">{PRESET_LABELS[l.preset]}</div>
                <div className="text-dim" style={{ fontSize: 'var(--fs-sm)' }}>
                  {zoneSummary(l, playlistName)}
                </div>
              </div>
              {perms.canManageContent && (
                <div className="row layout-row-actions">
                  <Button size="sm" onClick={() => setEditing(l)}>
                    Edit
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => setConfirmDelete(l)}>
                    Delete
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {editing && (
        <LayoutFormDrawer
          layout={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete layout?"
          message={`“${confirmDelete.name}” will be removed. Screens using it fall back to full-screen.`}
          confirmLabel="Delete"
          danger
          busy={del.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            del.mutate(confirmDelete.id, {
              onSuccess: () => {
                toast.success('Layout deleted');
                setConfirmDelete(null);
              },
              onError: (e) => {
                // 409 when a screen still references it (docs/03) — a clear, actionable message.
                toast.error(
                  e instanceof ApiError && e.isConflict
                    ? 'This layout is assigned to a screen. Change that screen’s layout first.'
                    : e instanceof ApiError
                      ? e.message
                      : 'Delete failed',
                );
                setConfirmDelete(null);
              },
            })
          }
        />
      )}
    </div>
  );
}

/** Create or edit a layout. `layout=null` creates. */
function LayoutFormDrawer({ layout, onClose }: { layout: Layout | null; onClose: () => void }) {
  const playlists = usePlaylists();
  const create = useCreateLayout();
  const update = useUpdateLayout();

  const [name, setName] = useState(layout?.name ?? '');
  const [preset, setPreset] = useState<LayoutPreset>(layout?.preset ?? 'main_ticker');
  const [zones, setZones] = useState<LayoutZones>(layout?.zones ?? {});
  const [error, setError] = useState<string | null>(null);

  const busy = create.isPending || update.isPending;
  const zoneIds = nonMainZonesForPreset(preset);
  const editingExisting = layout !== null;

  function setZone(id: 'side' | 'ticker', playlistId: string) {
    setZones((prev) => {
      const next = { ...prev };
      if (playlistId) next[id] = playlistId;
      else delete next[id];
      return next;
    });
  }

  function submit() {
    setError(null);
    if (!name.trim()) {
      setError('Give the layout a name.');
      return;
    }
    // Only send zones the chosen preset actually has (switching presets can leave
    // stale keys in state).
    const cleanZones: LayoutZones = {};
    for (const id of zoneIds) if (zones[id]) cleanZones[id] = zones[id];
    const onError = (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not save the layout.');
    if (layout) {
      // Preset is fixed once created (contract: updateLayoutSchema takes name/zones only).
      update.mutate(
        { id: layout.id, body: { name: name.trim(), zones: cleanZones } },
        { onSuccess: () => { toast.success('Layout saved'); onClose(); }, onError },
      );
    } else {
      create.mutate(
        { name: name.trim(), preset, zones: cleanZones },
        { onSuccess: () => { toast.success('Layout created'); onClose(); }, onError },
      );
    }
  }

  return (
    <Drawer title={layout ? 'Edit layout' : 'New layout'} onClose={onClose}>
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Storefront ticker"
      />

      <div className="field">
        <label>Layout</label>
        <div className="preset-picker" role="radiogroup" aria-label="Layout preset">
          {LAYOUT_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={preset === p}
              className={`preset-tile${preset === p ? ' selected' : ''}`}
              onClick={() => !editingExisting && setPreset(p)}
              disabled={editingExisting && preset !== p}
            >
              <PresetDiagram preset={p} showLabels />
              <span className="preset-tile-label">{PRESET_LABELS[p]}</span>
            </button>
          ))}
        </div>
        {editingExisting && <span className="hint">The preset is fixed once a layout is created — make a new layout to change it.</span>}
      </div>

      {zoneIds.length > 0 ? (
        <>
          {zoneIds.map((z) => (
            <div className="field" key={z}>
              <label htmlFor={`zone-${z}`}>{ZONE_LABELS[z]} playlist</label>
              <select
                id={`zone-${z}`}
                value={zones[z] ?? ''}
                onChange={(e) => setZone(z, e.target.value)}
              >
                <option value="">None</option>
                {(playlists.data ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <p className="hint">Video plays only in the main zone. Use images, templates, or a URL in side and ticker zones.</p>
        </>
      ) : (
        <p className="hint">Fullscreen shows a single zone — no extra playlists to assign.</p>
      )}

      {error && <p className="error-text" role="alert">{error}</p>}

      <div className="row mt-4">
        <Button variant="primary" onClick={submit} disabled={busy}>
          {layout ? 'Save layout' : 'Create layout'}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </Drawer>
  );
}
